import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTable, useSortBy, useGlobalFilter, usePagination, useRowSelect, useExpanded } from 'react-table';

import Autocomplete from './Autocomplete';
import Button from './Button';
import Checkbox from './Checkbox';
import Div from './Div';
import Icon from './Icon';
import Input from './Input';
import Label from './Label';
import Text from './Text';

/* eslint-disable react/prop-types */

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => (
  <Div w="25rem" d="flex" align="center" justify="center" m="auto" p={{ t: '1.25rem', b: '0' }}>
    <Input
      p={{ x: '1.5rem', y: '.5rem' }}
      h="1.875rem"
      type="text"
      name="filter"
      value={globalFilter || ''}
      onChange={(e) => {
        setGlobalFilter(e.target.value || undefined);
      }}
      rounded={{ l: 'sm' }}
      suffix={
        <Button h="1.875rem" rounded={{ r: 'sm' }} bg="brand500" hoverBg="brand600">
          <Icon name="Search" color="white" />
        </Button>
      }
    />
  </Div>
);

const SelectColumn = {
  id: 'selection',
  groupByBoundary: true,
  disableSortBy: true,
  Header: () => <Div />,
  Cell: ({ row }) => {
    const { checked, selectionTooltip, enableSelection } = row.getToggleRowSelectedProps();
    const isEnabled = enableSelection(row);
    return (
      <Div
        w="100%"
        d="flex"
        align="center"
        justify="center"
        data-tip={selectionTooltip(row)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isEnabled) {
            const { checked: _checked, onRowSelect, onRowDeselect } = row.getToggleRowSelectedProps();
            if (!_checked) {
              onRowSelect(row);
            } else {
              onRowDeselect(row);
            }
          }
        }}
      >
        <Label cursor={isEnabled ? 'pointer' : 'not-allowed'}>
          <Checkbox checked={checked} size=".75rem" disabled={!isEnabled} />
        </Label>
      </Div>
    );
  },
};

export const ExpanderColumn = {
  id: 'expander',
  disableSortBy: true,
  Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
    <Div
      tag="span"
      p={{ x: '1em' }}
      data-tip={isAllRowsExpanded ? 'Close expanded rows' : 'Expand all rows'}
      {...getToggleAllRowsExpandedProps()}
    >
      {isAllRowsExpanded ? (
        <Icon name="ArrowDown" color="tabSelected" size=".75rem" />
      ) : (
        <Icon name="ArrowRight" color="tabSelected" size=".75rem" />
      )}
    </Div>
  ),
  Cell: ({ row }) =>
    row.canExpand ? (
      <Div
        className="expander"
        d="flex"
        align="center"
        justify="center"
        w="100%"
        // p={{ x: '1em' }}
        m=".125rem"
        {...row.getToggleRowExpandedProps()}
        onClick={(e) => {
          const { onClick: _onClick } = row.getToggleRowExpandedProps();
          e.preventDefault();
          e.stopPropagation();
          _onClick(e);
        }}
      >
        {row.isExpanded ? (
          <Icon name="ArrowDown" color="tabSelected" size=".75rem" />
        ) : (
          <Icon name="ArrowRight" color="tabSelected" size=".75rem" />
        )}
      </Div>
    ) : (
      <Div w="100%" h="100%" />
    ),
};

const STATES = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
};

const AutocompleteEditableCell = ({
  value: initialValue,
  row,
  row: { original },
  column: { onEdit, Cell, isEditable, options },
}) => {
  const [isEdit, setEdit] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);

  const onSelection = (index) => {
    setValue(options[index]);
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <>
      {isEdit ? (
        <>
          <Autocomplete
            isOpen={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            menu={options}
            value={value}
            onSelection={onSelection}
          />
          <Div d="flex" flexDir="column-reverse" justify="flex-start">
            <Div
              m={{ y: '0', x: '.125rem' }}
              h="1rem"
              w="1rem"
              border="1px solid"
              borderColor="tabRest"
              p="0"
              rounded="sm"
            >
              <Icon
                m="0"
                size="1rem"
                name="Checked"
                cursor="pointer"
                color="tabRest"
                onClick={() => {
                  if (onEdit) {
                    onEdit(original, value);
                  }
                  setEdit(false);
                }}
              />
            </Div>
            <Div d="none" h="1rem" w="1rem" p="0" m="1px" border="1px solid" borderColor="tabRest" rounded="sm">
              <Icon
                m="0"
                size="1rem"
                name="Cross"
                cursor="pointer"
                color="tabRest"
                onClick={() => {
                  setEdit(false);
                }}
              />
            </Div>
          </Div>
        </>
      ) : isEditable === STATES.DISABLED ? (
        <>
          <Div p="0" rounded="sm" m={{ y: '.125rem', r: '.25rem', l: '.125rem' }}>
            <Icon m="0" size=".5rem" name="DownArrow" cursor="not-allowed" color="tabRest" />
          </Div>
          <Cell value={value} row={row} />
        </>
      ) : (
        <>
          <Div h="1.5rem" w="1.5rem" p="0" rounded="sm" m={{ y: '.125rem', r: '.25rem', l: '.125rem' }}>
            <Icon
              size=".5rem"
              p=".5rem"
              name="DownArrow"
              cursor="pointer"
              color="tabSelected"
              onClick={() => {
                setEdit(true);
              }}
              data-tip="Choose"
            />
          </Div>
          <Cell value={value} row={row} />
        </>
      )}
    </>
  );
};

const EditableCell = ({
  value: initialValue,
  row,
  row: { original },
  column: { onEdit, Cell, isEditable, id: colId },
}) => {
  const [isEdit, setEdit] = useState(false);
  const [value, setValue] = useState(initialValue);

  const onChange = (e) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <>
      {isEdit ? (
        <>
          <Input value={value} onChange={onChange} />
          <Div d="flex" flexDir="column">
            <Div m="1px" h="1rem" w="1rem" border="1px solid" borderColor="tabRest" p="0" rounded="sm">
              <Icon
                m="0"
                size="1rem"
                name="Checked"
                cursor="pointer"
                color="tabSelected"
                onClick={() => {
                  if (onEdit) {
                    onEdit(original, value);
                  }
                  setEdit(false);
                }}
              />
            </Div>
          </Div>
        </>
      ) : isEditable === STATES.DISABLED ? (
        <>
          <Div
            h="1.5rem"
            w="1.5rem"
            rounded="sm"
            m={{
              r: '.375rem',
            }}
            d="flex"
            justify="center"
            align="center"
          >
            <Icon name="Edit" cursor="not-allowed" color="tabDisabled" size=".75rem" hoverTransform="scale(1.25)" />
          </Div>
          <Cell value={value} row={row} />
        </>
      ) : (
        <>
          <Div
            h="1.5rem"
            w="1.5rem"
            rounded="sm"
            m={{
              r: '.375rem',
            }}
            d="flex"
            justify="center"
            align="center"
          >
            <Icon
              name="Edit"
              cursor="pointer"
              color="lightGrey"
              data-tip={`Edit ${colId}`}
              size=".75rem"
              hoverTransform="scale(1.25)"
              onClick={() => {
                setEdit(true);
              }}
            />
          </Div>
          <Cell value={value} row={row} />
        </>
      )}
    </>
  );
};

const getHeaderProps = (props, { column }) => [
  props,
  {
    justify: column.justify || 'center',
    p: column.padding || {},
  },
];

const getCellProps = (props, { cell }) => [
  props,
  {
    justify: cell.column.cellJustify || 'flex-start',
    p: cell.column.cellPadding || {},
  },
];

const BaseStyledHeader = (props) => <Div {...props} tag="tr" />;

const StyledHeader = styled(BaseStyledHeader)`
  & > th:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  & > th:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const StyledTableHeadingCell = (props) => <Div {...props} tag="th" />;

const TableHeadingCell = ({ children, ...rest }) => (
  <StyledTableHeadingCell
    d="flex"
    align="center"
    justify="center"
    bg="secondary"
    textColor="black"
    textSize="subtitle"
    textWeight="600"
    h="2.25rem"
    p={{ y: '1rem', x: '1rem' }}
    m={{ b: '.625rem' }}
    textTransform="capitalize"
    {...rest}
  >
    {children}
  </StyledTableHeadingCell>
);

const BaseStyledRow = (props) => <Div {...props} tag="tr" />;
const StyledRow = styled(BaseStyledRow)`
  & > td {
    background-color: white;
  }
  tr.sub-row + &.row > td {
    margin-top: 1rem;
  }
  tbody > &.row > td {
    border-top: 1px solid #e6e6e6;
  }
  tr.row + &.row > td {
    border-top: 1px solid #e6e6e6;
  }
`;

const StyledSubRow = styled(BaseStyledRow)`
  tr.row + &.sub-row > td {
    margin-top: 1rem;
  }
  & > td:nth-child(2) {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  & > td:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const StyledBottomBorderShell = styled(Div)`
  display: contents;
  .sub-row + & > .bottomBorder {
    margin-top: 1rem;
  }
`;

const StyledBottomBorder = styled(Div)`
  width: 100%;
  height: 1px;
  background-color: #e6e6e6;
`;

const Row = ({ children, ...rest }) => (
  <StyledRow className="row" d="contents" {...rest}>
    {children}
  </StyledRow>
);

const SubRow = ({ children, ...rest }) => (
  <StyledSubRow className="sub-row" d="contents" {...rest}>
    {children}
  </StyledSubRow>
);

const StyledCell = (props) => <Div {...props} tag="td" />;

const Cell = ({ children, subRowExpanderCell, depth, ...rest }) => (
  <StyledCell
    d="flex"
    align="center"
    justify="flex-start"
    textColor="black"
    textSize="subtitle"
    textWeight="400"
    bg={subRowExpanderCell ? 'white' : 'secondary'}
    border={{ b: '1px solid' }}
    borderColor="white"
    h="1.875rem"
    p={{ x: '.5rem' }}
    {...rest}
  >
    {children}
  </StyledCell>
);

export const Pagination = ({
  canPreviousPage,
  canNextPage,
  pageOptions,
  pageCount,
  gotoPage,
  nextPage,
  previousPage,
  setPageSize,
  pageIndex,
  pageSize,
  menuDirection,
}) => {
  const [page, setPage] = useState(pageIndex + 1);
  const [isOpen, setIsOpen] = useState(false);
  const RECORDS = ['10', '20', '30', '40', '50'];
  return (
    <Div d="flex" justify="space-between" align="center" p={{ b: '1.5rem', t: '.5rem', x: '1.5rem' }}>
      <Div d="flex" m={{ x: '-.5rem' }}>
        <Button
          bg="brand500"
          hoverBg="brand600"
          m={{ x: '.5rem' }}
          h="1.875rem"
          disabled={!canPreviousPage}
          onClick={() => {
            gotoPage(0);
          }}
          data-tip="first"
        >
          <Icon color="white" name="Back" />
        </Button>
        <Button
          bg="brand500"
          hoverBg="brand600"
          m={{ x: '.5rem' }}
          h="1.875rem"
          disabled={!canPreviousPage}
          onClick={() => {
            previousPage();
          }}
          data-tip="previous"
        >
          <Icon color="white" name="LeftArrow" />
        </Button>
        <Div m={{ x: '.5rem' }} d="flex" align="center" justify="center">
          <Text textSize="body">{`Page ${pageIndex + 1} of ${pageOptions.length}`}</Text>
        </Div>
        <Button
          textColor="white"
          bg="brand500"
          hoverBg="brand600"
          m={{ x: '.5rem' }}
          h="1.875rem"
          disabled={!canNextPage}
          onClick={() => {
            nextPage();
          }}
          data-tip="next"
        >
          <Icon color="white" name="RightArrow" />
        </Button>
        <Button
          bg="brand500"
          hoverBg="brand600"
          m={{ x: '.5rem' }}
          h="1.875rem"
          disabled={!canNextPage}
          onClick={() => {
            gotoPage(pageCount - 1);
          }}
          data-tip="last"
        >
          <Icon color="white" name="Next" />
        </Button>
      </Div>
      <Div d="flex" align="center">
        <Text p={{ x: '.5rem' }} textSize="body">
          Go To Page
        </Text>
        <Input
          w="4rem"
          h="1.875rem"
          type="number"
          value={page}
          onChange={(e) => {
            setPage(e.target.value);
          }}
        />
        <Button
          bg="brand500"
          hoverBg="brand600"
          m={{ x: '.5rem' }}
          h="1.875rem"
          disabled={Number(page) < 0 || Number(page) > pageCount}
          onClick={() => {
            gotoPage(Number(page) - 1);
          }}
          data-tip="jump"
        >
          <Text textSize="body" color="white">
            Go
          </Text>
        </Button>
      </Div>
      <Div d="flex" align="center">
        <Text textSize="body" p={{ r: '.5rem' }}>
          Show
        </Text>
        <Autocomplete
          w="12.5rem"
          h="1.875rem"
          hoverBg="gray200"
          focusBg="gray200"
          bg="white"
          isOpen={isOpen}
          value={pageSize.toString()}
          onClick={() => setIsOpen(!isOpen)}
          DropdownButtonOptions={{
            h: '1.875rem',
          }}
          inputStyle={{
            h: 'calc(1.875rem-1px)',
          }}
          menuOptions={{
            pos: 'absolute',
            shadow: 4,
            minW: '100%',
            rounded: 'sm',
            maxH: '15rem',
            bg: 'white',
            overflow: 'auto',
          }}
          DivOptions={{
            bg: 'white',
          }}
          direction={menuDirection ?? 'topright'}
          menu={RECORDS}
          onSelection={(index) => {
            setPageSize(RECORDS[index]);
          }}
        />
        <Text p={{ l: '.5rem' }} textSize="body">
          records per page
        </Text>
      </Div>
    </Div>
  );
};

const StyledTable = (props) => <Div {...props} tag="table" />;

const TableComponent = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  rows,
  prepareRow,
  onRowClick,
  onRowSelect,
  onRowDeselect,
  onRowExpand,
  hasExpansion,
  numCols,
  paginate,
  canPreviousPage,
  canNextPage,
  pageOptions,
  pageCount,
  gotoPage,
  nextPage,
  previousPage,
  setPageSize,
  state,
  widths,
  ...rest
}) => (
  <>
    <StyledTable
      {...getTableProps()}
      d="grid"
      justify="left"
      align="center"
      minW="100%"
      p={{ y: '1.25rem' }}
      overflow="auto"
      style={{
        gridTemplateColumns: widths || `repeat(${numCols || '1fr'}, 1fr)`,
      }}
      {...rest}
    >
      <thead style={{ display: 'contents' }}>
        {headerGroups.map((headerGroup) => (
          <StyledHeader className="header" {...headerGroup.getHeaderGroupProps()} style={{ display: 'contents' }}>
            {headerGroup.headers.map((column) => (
              <TableHeadingCell
                {...column.getHeaderProps(column.getSortByToggleProps())}
                onClick={() => column.toggleSortBy?.(!column.isSortedDesc)}
              >
                {column.render('Header')}
                {!column.disableSortBy ? (
                  <Div d="flex" flexDir="column" m={{ l: '.4rem' }}>
                    <Icon
                      size=".5rem"
                      name="Play"
                      transform="rotate(-90deg)"
                      color={column.isSorted ? (column.isSortedDesc ? 'tabRest' : 'tabSelected') : 'tabRest'}
                    />
                    <Icon
                      size=".5rem"
                      name="Play"
                      transform="rotate(90deg)"
                      color={column.isSorted ? (!column.isSortedDesc ? 'tabRest' : 'tabSelected') : 'tabRest'}
                    />
                  </Div>
                ) : (
                  ''
                )}
              </TableHeadingCell>
            ))}
          </StyledHeader>
        ))}
      </thead>
      <tbody style={{ display: 'contents' }} {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const isSubRow = row.depth !== 0;
          const additionalProps = {};
          if (isSubRow) {
            const [parentRowId, childRowId] = row.id.split('.').map((id) => parseInt(id, 10));
            const numSubRows = rows[parentRowId].subRows.length;
            const firstRow = childRowId === 0;
            const lastRow = childRowId === numSubRows - 1;
            additionalProps.firstRow = firstRow;
            additionalProps.lastRow = lastRow;
          }
          const Component = isSubRow ? SubRow : Row;
          return (
            <Component onClick={() => {}} {...row.getRowProps()} depth={row.depth} {...additionalProps}>
              {row.cells.map((cell, i) => {
                const { subRowsColsIndentIndexes, ...cellProps } = cell.getCellProps([{ style: cell.column.style }]);
                const {
                  column: { options, isEditable },
                } = cell;
                return (
                  <Cell
                    {...cellProps}
                    depth={subRowsColsIndentIndexes.includes(i) ? row.depth : 0}
                    subRowExpanderCell={isSubRow && i === 0}
                  >
                    {isEditable
                      ? options
                        ? cell.render('AutocompleteEditableCell')
                        : cell.render('EditableCell')
                      : cell.render('Cell')}
                  </Cell>
                );
              })}
            </Component>
          );
        })}
        <StyledBottomBorderShell className="bottomBorderShell">
          {new Array(numCols).fill(true).map(() => (
            <StyledBottomBorder className="bottomBorder" />
          ))}
        </StyledBottomBorderShell>
      </tbody>
    </StyledTable>
    {paginate ? (
      <Pagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageOptions={pageOptions}
        pageCount={pageCount}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        pageIndex={state.pageIndex}
        pageSize={state.pageSize}
      />
    ) : null}
  </>
);

const Table = ({
  columns,
  data,
  gridState,
  controlledGridState,
  onRowClick,
  onRowSelect,
  onRowDeselect,
  onRowExpand,
  hasGlobalFilter,
  hasSelection,
  enableSelection,
  selectionTooltip,
  hasExpansion,
  setGridState,
  subRowsColsIndentIndexes,
  paginate,
  widths,
  ...rest
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: {
        EditableCell,
        AutocompleteEditableCell,
      },
      selectSubRows: false,
      initialState: gridState,
      stateReducer: (_state) => {
        const { expanded } = _state;
        const newState = {
          ..._state,
          ...controlledGridState,
        };
        const anyRowOpen = Object.keys(expanded)
          .map((key) => expanded[key])
          .some((elem) => elem);
        onRowExpand(anyRowOpen);
        setGridState(newState);
        return newState;
      },
      useControlledState: (_state) =>
        useMemo(
          () => ({
            ..._state,
            ...controlledGridState,
          }),
          [_state, controlledGridState]
        ),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.getToggleRowSelectedProps.push({
        onRowSelect: (row) => {
          onRowSelect(row);
        },
        onRowDeselect: (row) => {
          onRowDeselect(row);
        },
        enableSelection: (row) => enableSelection(row),
        selectionTooltip: (row) => selectionTooltip(row),
      });
      if (hasSelection) {
        hooks.visibleColumns.push((_columns) => {
          const hasExpander = _columns[0].id === 'expander';
          if (hasExpander) {
            const [expanderColumn, ..._rest] = _columns;
            return [expanderColumn, SelectColumn, ..._rest];
          }
          return [SelectColumn, ..._columns];
        });
      }
      hooks.getCellProps.push(getCellProps);
      hooks.getCellProps.push({
        subRowsColsIndentIndexes,
      });
      hooks.getHeaderProps.push(getHeaderProps);
    }
  );
  return data && data.length > 0 ? (
    <Div>
      {hasGlobalFilter ? <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} /> : null}
      <TableComponent
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        rows={paginate ? page : rows}
        prepareRow={prepareRow}
        onRowClick={({ original }) => {
          onRowClick(original);
        }}
        onRowSelect={({ original }) => {
          onRowSelect(original);
        }}
        onRowDeselect={({ original }) => {
          onRowDeselect(original);
        }}
        numCols={hasSelection ? 1 + columns.length : columns.length}
        hasExpansion
        state={state}
        paginate={paginate}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageOptions={pageOptions}
        pageCount={pageCount}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        widths={widths}
        {...rest}
      />
    </Div>
  ) : (
    <Div>No Data</Div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  gridState: PropTypes.shape({}),
  controlledGridState: PropTypes.shape({}),
  onRowClick: PropTypes.func,
  onRowSelect: PropTypes.func,
  onRowDeselect: PropTypes.func,
  onRowExpand: PropTypes.func,
  hasGlobalFilter: PropTypes.bool,
  hasSelection: PropTypes.bool,
  enableSelection: PropTypes.func,
  selectionTooltip: PropTypes.func,
  hasExpansion: PropTypes.bool,
  setGridState: PropTypes.func,
  subRowsColsIndentIndexes: PropTypes.arrayOf(PropTypes.number),
  paginate: PropTypes.bool,
  widths: PropTypes.string,
};

Table.defaultProps = {
  gridState: {},
  controlledGridState: {},
  onRowClick: () => null,
  onRowSelect: () => null,
  onRowDeselect: () => null,
  onRowExpand: () => null,
  hasGlobalFilter: false,
  hasSelection: false,
  enableSelection: () => true,
  selectionTooltip: () => null,
  hasExpansion: false,
  setGridState: () => null,
  subRowsColsIndentIndexes: [],
  paginate: false,
  widths: null,
};

export default Table;
